import { useMutation, useQueryClient } from "@tanstack/react-query";
import { mcpClient } from "../../lib/mcp-client";
import { queryKeys } from "../../lib/query-keys";

/**
 * Sprint 14g — archive / restore / delete for Library nodes (Pages, Memories,
 * Prompts). archive_node + restore_node already existed; delete_node is the new
 * owner-only hard delete. Shared across the three library-node cockpit panels so
 * the cache-invalidation set stays in one place.
 */

function unwrap<T>(result: { isError?: boolean; structuredContent?: T; content?: Array<{ text?: string }> }): T {
  if (result.isError) throw new Error(result.content?.[0]?.text ?? "Request failed");
  return result.structuredContent as T;
}

function invalidateNodes(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["child-nodes"] });
  qc.invalidateQueries({ queryKey: queryKeys.libraryTree() });
  qc.invalidateQueries({ queryKey: queryKeys.workspacePages() });
  qc.invalidateQueries({ queryKey: ["prompts-in-scope"] });
  qc.invalidateQueries({ queryKey: ["all-prompts"] });
  qc.invalidateQueries({ queryKey: ["mcp-prompts"] });
}

export function useArchiveNodeAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, cascade }: { id: string; cascade?: boolean }): Promise<void> => {
      const r = await mcpClient.toolsCall("archive_node", { id, ...(cascade === undefined ? {} : { cascade }) });
      unwrap(r);
    },
    onSuccess: () => invalidateNodes(qc),
  });
}

export function useRestoreNodeAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, cascade }: { id: string; cascade?: boolean }): Promise<void> => {
      const r = await mcpClient.toolsCall("restore_node", { id, ...(cascade === undefined ? {} : { cascade }) });
      unwrap(r);
    },
    onSuccess: () => invalidateNodes(qc),
  });
}

export function useDeleteNodeAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, cascade, force }: { id: string; cascade?: boolean; force?: boolean }): Promise<void> => {
      const r = await mcpClient.toolsCall("delete_node", {
        id,
        ...(cascade ? { cascade: true } : {}),
        ...(force ? { force: true } : {}),
      });
      unwrap(r);
    },
    onSuccess: () => invalidateNodes(qc),
  });
}
